import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { usePortal } from "../../../../context/PortalsContext";
import {
  AssignmentDependency,
  AssignmentType,
  DdlAssignment,
  PackageType,
} from "../../../../services/api/types/assignments/assignment";
import { brand, error, gray } from "../../../../theme/themePrimitives";
import {
  MAX_LENGTH_SHORT_LABEL,
  MAX_LENGTH_TAG,
  MAX_LENGTH_TITLE,
} from "../../../../utils/inputLimits";
import { ChangeEventType, FormErrors, FormValues } from "../AssignmentUploads";
import { ReadingExperienceSection } from "./ReadingExperienceSection";
import { TranslateFieldModal } from "./TranslateFieldModal";
import { ReactComponent as TranslateIcon } from "../../../../assets/translate.svg";

interface MainAdminFormProps {
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  errors: FormErrors;
  handleChange: (e: ChangeEventType) => void;
  handleSave: () => void;
  buttonLoading: boolean;
  isEditMode: boolean;
  isCustom: Boolean;
  assignmentCreated: boolean;
  dependencyOptions: DdlAssignment[];
}

const assignmentTypes: { name: string; value: AssignmentType }[] = [
  { name: "Course", value: AssignmentType.COURSE },
  { name: "Survey", value: AssignmentType.SURVEY },
  { name: "Policy", value: AssignmentType.POLICY },
];

const packageTypes: { name: string; value: PackageType }[] = [
  { name: "SCORM 2004", value: PackageType.Scorm2004 },
  { name: "SCORM 1.2", value: PackageType.Scorm12 },
  { name: "Gomo Wrapper SCORM 1.2", value: PackageType.GomoScorm12 },
  { name: "Gomo Wrapper SCORM 2004", value: PackageType.GomoScorm2004 },
  { name: "xApi", value: PackageType.xApi },
];

const pdfPackageType: { name: string; value: PackageType } = {
  name: "PDF Uploads",
  value: PackageType.PdfUpload,
};

const videoPackageType = {
  name: "Video Uploads",
  value: PackageType.VideoUpload,
};

const TAG_MAX_LENGTH = MAX_LENGTH_TAG;
const TAG_MAX_COUNT = 500;

export const MainAdminForm: React.FC<MainAdminFormProps> = ({
  formValues,
  errors,
  handleChange,
  handleSave,
  setFormValues,
  buttonLoading,
  isEditMode,
  isCustom,
  assignmentCreated,
  dependencyOptions,
}) => {
  const { portals } = usePortal();

  const [tagError, setTagError] = useState("");
  const [titleModalOpen, setTitleModalOpen] = useState(false);
  const [titleShortModalOpen, setTitleShortModalOpen] = useState(false);

  const titleTranslations = Object.fromEntries(
    formValues.titleTranslation.map((t) => [t.languageCode, t.title]),
  );
  const titleShortTranslations = Object.fromEntries(
    formValues.titleShortTranslation.map((t) => [
      t.languageCode,
      t.titleShort,
    ]),
  );

  const handleSaveTitleTranslations = (translations: Record<string, string>) =>
    setFormValues((prev) => ({
      ...prev,
      titleTranslation: Object.entries(translations)
        .filter(([code, title]) => code !== "en" && title.trim() !== "")
        .map(([languageCode, title]) => ({ languageCode, title })),
    }));

  const handleSaveTitleShortTranslations = (
    translations: Record<string, string>,
  ) =>
    setFormValues((prev) => ({
      ...prev,
      titleShortTranslation: Object.entries(translations)
        .filter(([code, titleShort]) => code !== "en" && titleShort.trim() !== "")
        .map(([languageCode, titleShort]) => ({ languageCode, titleShort })),
    }));

  // Show toggle for custom assignments in non-main portals
  // Show for all non-main portals (assignments created there are typically custom)
  const showSubPortalsToggle =
    !portals.selectedPortal?.isMain && !portals.selectedPortal?.isSubPortal;

  const isCourseAssignmentType =
    formValues.assignmentType === AssignmentType.COURSE;
  const isPolicyAssignmentType =
    formValues.assignmentType === AssignmentType.POLICY;

  const availablePackageTypes = isPolicyAssignmentType
    ? [pdfPackageType]
    : isCourseAssignmentType
      ? [...packageTypes, pdfPackageType, videoPackageType]
      : packageTypes;

  const isPdfUpload = formValues.packageType === PackageType.PdfUpload;
  const isVideoUpload = formValues.packageType === PackageType.VideoUpload;

  // Once Continue is clicked, the assignment draft is created on the backend
  // with this type/package combination; going back to step 1 must not allow
  // changing it again, whether or not a package was actually uploaded yet.
  const typeFieldsLocked = isEditMode || assignmentCreated;

  // Keep the selected package type in sync with what's available for the
  // current assignment type: auto-select the sole PDF option for Policy,
  // and clear a now-unavailable selection for the other types.
  useEffect(() => {
    if (isPolicyAssignmentType) {
      if (!isPdfUpload) {
        setFormValues((prev) => ({
          ...prev,
          packageType: PackageType.PdfUpload,
        }));
      }
      return;
    }
    if (
      isPdfUpload &&
      !availablePackageTypes.some((p) => p.value === formValues.packageType)
    ) {
      setFormValues((prev) => ({
        ...prev,
        packageType: undefined,
        pageByPage: false,
        acknowledgement: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.assignmentType]);

  // pageByPage and mandatory only apply to PDF/Video packages respectively;
  // clear a stale value left over from a previously selected package type.
  useEffect(() => {
    if (!isPdfUpload && formValues.pageByPage) {
      setFormValues((prev) => ({ ...prev, pageByPage: false }));
    }
    if (!isVideoUpload && formValues.mandatory) {
      setFormValues((prev) => ({ ...prev, mandatory: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.packageType]);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 4 }}>
        <Stack spacing={3} width={"100%"}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Assignment type
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <Select
              id="assignment-upload-type-select"
              disabled={typeFieldsLocked}
              name="assignmentType"
              IconComponent={ExpandMoreIcon}
              value={formValues.assignmentType || ""}
              error={Boolean(errors.assignmentType)}
              onChange={handleChange}
              displayEmpty
              renderValue={(selected) =>
                selected ? (
                  assignmentTypes.find((t) => t.value === selected)?.name
                ) : (
                  <Typography color="text.disabled">
                    Select assignment type
                  </Typography>
                )
              }
              sx={{
                borderRadius: "8px",
                minWidth: "150px",
                backgroundColor: "transparent",
                height: "44px",
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  padding: "8.5px 14px",
                },
              }}
              size="medium"
            >
              {assignmentTypes.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
            {errors.assignmentType && (
              <Typography
                sx={{ margin: "4px 4px 0px !important" }}
                fontSize={"12px"}
                color={error[600]}
              >
                {errors.assignmentType}
              </Typography>
            )}
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Package type
              {!isPolicyAssignmentType && (
                <Box component="span" sx={{ color: `var(--error-700)` }}>
                  *
                </Box>
              )}
            </Typography>
            <Select
              id="assignment-upload-package-type-select"
              disabled={typeFieldsLocked}
              name="packageType"
              IconComponent={ExpandMoreIcon}
              value={formValues.packageType || ""}
              error={Boolean(errors.packageType)}
              onChange={handleChange}
              displayEmpty
              renderValue={(selected) =>
                selected ? (
                  availablePackageTypes.find((t) => t.value === selected)?.name
                ) : (
                  <Typography color="text.disabled">
                    Select package type
                  </Typography>
                )
              }
              sx={{
                borderRadius: "8px",
                minWidth: "150px",
                backgroundColor: "transparent",
                height: "44px",
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  padding: "8.5px 14px",
                },
              }}
              size="medium"
            >
              {availablePackageTypes.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
            {errors.packageType && (
              <Typography
                sx={{ margin: "4px 4px 0px !important" }}
                fontSize={"12px"}
                color={error[600]}
              >
                {errors.packageType}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack spacing={3} width={"100%"}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              SKU
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              id="sku"
              name="SKU"
              placeholder="Enter SKU"
              type="text"
              value={formValues.SKU}
              onChange={handleChange}
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
              {...(!isCustom
                ? { error: Boolean(errors.SKU), helperText: errors.SKU }
                : {})}
              slotProps={{
                input: {
                  endAdornment: !isCustom && errors.SKU && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
                htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Code
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              id="code"
              name="code"
              placeholder="Enter code"
              type="text"
              value={formValues.code}
              onChange={handleChange}
              variant="outlined"
              size="small"
              error={Boolean(errors.code)}
              helperText={errors.code}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
              slotProps={{
                input: {
                  endAdornment: errors.code && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
                htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
              }}
            />
          </Stack>
        </Stack>
      </Box>
      {(isPdfUpload || isVideoUpload) && (
        <ReadingExperienceSection
          formValues={formValues}
          setFormValues={setFormValues}
          isPdfUpload={isPdfUpload}
          isVideoUpload={isVideoUpload}
        />
      )}
      <Box sx={{ display: "flex", gap: 4, mt: 2 }}>
        <Stack spacing={3} width={"100%"}>
          <Stack spacing={1}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="subtitle2">
                  Primary title
                  <Box component="span" sx={{ color: `var(--error-700)` }}>
                    *
                  </Box>
                </Typography>
              </Box>
              <TranslateIcon
                onClick={() =>
                  formValues.title.trim() !== "" && setTitleModalOpen(true)
                }
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "2px",
                  borderRight:
                    formValues.title.trim() !== ""
                      ? `1px solid ${brand[600]}`
                      : "1px solid #98A2B3",
                  borderBottom:
                    formValues.title.trim() !== ""
                      ? `1px solid ${brand[600]}`
                      : "1px solid #98A2B3",
                  cursor:
                    formValues.title.trim() !== "" ? "pointer" : "default",
                  color:
                    formValues.title.trim() !== "" ? brand[600] : "#98A2B3",
                }}
              />
            </Box>
            <TextField
              fullWidth
              id="title"
              name="title"
              placeholder="Enter the title in English first"
              type="text"
              value={formValues.title}
              onChange={handleChange}
              variant="outlined"
              size="small"
              error={Boolean(errors.title)}
              helperText={errors.title}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
              slotProps={{
                input: {
                  endAdornment: errors.title && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
                htmlInput: { maxLength: MAX_LENGTH_TITLE },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2">Thumbnail title</Typography>
              <TranslateIcon
                onClick={() =>
                  formValues.titleShort.trim() !== "" &&
                  setTitleShortModalOpen(true)
                }
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "2px",
                  borderRight:
                    formValues.titleShort.trim() !== ""
                      ? `1px solid ${brand[600]}`
                      : "1px solid #98A2B3",
                  borderBottom:
                    formValues.titleShort.trim() !== ""
                      ? `1px solid ${brand[600]}`
                      : "1px solid #98A2B3",
                  cursor:
                    formValues.titleShort.trim() !== "" ? "pointer" : "default",
                  color:
                    formValues.titleShort.trim() !== ""
                      ? brand[600]
                      : "#98A2B3",
                }}
              />
            </Box>
            <TextField
              fullWidth
              id="titleShort"
              name="titleShort"
              placeholder="Enter the title in English first"
              type="text"
              value={formValues.titleShort}
              onChange={handleChange}
              variant="outlined"
              size="small"
              error={Boolean(errors.titleShort)}
              helperText={errors.titleShort}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
              slotProps={{
                input: {
                  endAdornment: errors.titleShort ? (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ) : (
                    <Tooltip
                      title="Displayed on assignment cards. Defaults to Primary Title if left empty."
                      arrow
                      placement="right"
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: gray[800],
                          },
                        },
                        arrow: {
                          sx: {
                            color: gray[800],
                          },
                        },
                      }}
                    >
                      <HelpOutlineOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: "#98A2B3",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  ),
                },
                htmlInput: { maxLength: MAX_LENGTH_TITLE },
              }}
            />
          </Stack>
          {showSubPortalsToggle && (
            <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.isAvailableToSubportals}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        isAvailableToSubportals: e.target.checked,
                      }))
                    }
                  />
                }
                label="Available to subportals"
                sx={{
                  marginLeft: 0,
                  gap: "1rem",
                  "& .MuiFormControlLabel-label": {
                    color: "black",
                  },
                }}
              />
            </Box>
          )}
        </Stack>
        <Stack spacing={3} width={"100%"}>
          <Stack spacing={1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="subtitle2">Dependency</Typography>
              <Tooltip
                title="Defines assignment dependencies."
                arrow
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: gray[800],
                    },
                  },
                  arrow: {
                    sx: {
                      color: gray[800],
                    },
                  },
                }}
              >
                <HelpOutlineOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: "#98A2B3",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </Box>
            <Autocomplete<DdlAssignment, true>
              id="assignment-upload-dependency-autocomplete"
              multiple
              filterSelectedOptions
              options={dependencyOptions}
              value={dependencyOptions.filter((o) =>
                formValues.dependency.some(
                  (d) => d.prerequisiteAssignmentUid === o.AssignmentUid,
                ),
              )}
              size="small"
              isOptionEqualToValue={(a, b) =>
                a.AssignmentUid === b.AssignmentUid
              }
              getOptionLabel={(o) => o.title}
              onChange={(_, newValue) =>
                setFormValues((prev) => ({
                  ...prev,
                  dependency: newValue.map(
                    (o): AssignmentDependency => ({
                      prerequisiteAssignmentUid: o.AssignmentUid,
                      prerequisiteAssignmentId: o.AssignmentId,
                    }),
                  ),
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select assignments"
                  error={Boolean(errors.dependency)}
                  helperText={errors.dependency}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              )}
              renderValue={(value, getTagProps) => (
                <Box
                  component="span"
                  sx={{ display: "inline-flex", flexWrap: "wrap", gap: 1 }}
                >
                  {value.map((option, index) => {
                    const { key, ...rest } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        {...rest}
                        label={option.title}
                        sx={{
                          backgroundColor: "white",
                          padding: "14px 6px",
                          borderRadius: "6px",
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              slotProps={{
                popupIndicator: { sx: { border: "none" } },
                clearIndicator: { sx: { display: "none" } },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "22px",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: `${formValues.packageType === PackageType.PdfUpload || formValues.packageType === PackageType.VideoUpload ? gray[400] : "black"}`,
                }}
              >
                Max no. attempts
              </Typography>
              <FormControlLabel
                sx={{ mr: 0, ml: 0, height: "22px" }}
                slotProps={{ typography: { variant: "subtitle2" } }}
                control={
                  <Checkbox
                    size="small"
                    sx={{ p: "2px" }}
                    checked={formValues.attemptsUnlimited}
                    disabled={
                      formValues.packageType === PackageType.PdfUpload ||
                      formValues.packageType === PackageType.VideoUpload
                    }
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormValues((prev) => ({
                        ...prev,
                        attemptsUnlimited: checked,
                        maxAttempts: checked ? undefined : prev.maxAttempts,
                      }));
                    }}
                  />
                }
                label="Unlimited"
              />
            </Box>
            <TextField
              placeholder="Select no. attempts"
              type="number"
              disabled={
                formValues.packageType === PackageType.PdfUpload ||
                formValues.packageType === PackageType.VideoUpload ||
                formValues.attemptsUnlimited
              }
              value={formValues.attemptsUnlimited ? "" : formValues.maxAttempts ?? ""}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[^0-9]/g, "");
                setFormValues((prev) => ({
                  ...prev,
                  maxAttempts: sanitized === "" ? undefined : Number(sanitized),
                }));
              }}
              onKeyDown={(e) =>
                ["-", "+", "e", "E", "."].includes(e.key) && e.preventDefault()
              }
              error={Boolean(errors.maxAttempts)}
              helperText={errors.maxAttempts}
              slotProps={{ htmlInput: { min: 1 } }}
              size="small"
              sx={{
                "& .MuiInputBase-input": {
                  px: "14px",
                  py: "10px",
                  cursor: `${formValues.packageType === PackageType.PdfUpload || formValues.packageType === PackageType.VideoUpload ? "not-allowed" : "text"}`,
                },
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography>Tags</Typography>
            <Autocomplete
              id="assignment-upload-tags-autocomplete"
              multiple
              freeSolo
              options={[]}
              value={formValues.tags}
              onChange={(_, newValue) => {
                if (newValue.some((tag) => tag.length > TAG_MAX_LENGTH)) {
                  setTagError(
                    `Individual tags cannot exceed ${TAG_MAX_LENGTH} characters`,
                  );
                  return;
                }
                if (newValue.length > TAG_MAX_COUNT) {
                  setTagError(`You cannot add more than ${TAG_MAX_COUNT} tags`);
                  return;
                }
                if (new Set(newValue).size !== newValue.length) {
                  setTagError("Duplicate tags are not allowed");
                  return;
                }
                setTagError("");
                setFormValues((prev) => ({ ...prev, tags: newValue }));
              }}
              renderValue={(value: string[], getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={`${option}-${index}`}
                    label={option}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "6px",
                      maxWidth: "100%",
                      height: "auto",
                      maxHeight: "none",
                      "& .MuiChip-label": {
                        display: "block",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflow: "visible",
                        textOverflow: "clip",
                        py: "4px",
                      },
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  error={Boolean(errors.tags) || Boolean(tagError)}
                  helperText={tagError || errors.tags}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </Stack>
        </Stack>
      </Box>
      <Box mt={2} sx={{ display: "flex", justifyContent: "center" }}>
        {!isEditMode && (
          <>
            {buttonLoading ? (
              <CircularProgress />
            ) : (
              <Button
                onClick={handleSave}
                sx={{
                  px: 2,
                  borderRadius: "12px",
                  backgroundColor: `${brand[600]} !important`,
                  color: `white`,
                  "&:hover": {
                    bgcolor: `${brand[700]} !important`,
                  },
                }}
              >
                Continue
              </Button>
            )}
          </>
        )}
      </Box>
      <TranslateFieldModal
        open={titleModalOpen}
        onClose={() => setTitleModalOpen(false)}
        fieldLabel="Primary title"
        englishValue={formValues.title}
        onEnglishValueChange={(value) =>
          handleChange({
            target: { name: "title", value },
          } as ChangeEventType)
        }
        translations={titleTranslations}
        onSave={handleSaveTitleTranslations}
      />
      <TranslateFieldModal
        open={titleShortModalOpen}
        onClose={() => setTitleShortModalOpen(false)}
        fieldLabel="Thumbnail title"
        englishValue={formValues.titleShort}
        onEnglishValueChange={(value) =>
          handleChange({
            target: { name: "titleShort", value },
          } as ChangeEventType)
        }
        translations={titleShortTranslations}
        onSave={handleSaveTitleShortTranslations}
      />
    </Box>
  );
};
